import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Plus, X, Settings2, Send, Check, UserRound, Zap, Lightbulb } from "lucide-react";
import { useGuard } from "@/lib/guards";

// Lot 770 — réponses rapides : modèles d'équipe (⚡, gérés par le super admin) + « Mes modèles » (par agent), proposables à
// l'équipe (cloche aux super admins, qui approuvent → modèle d'équipe, ou refusent).
const STATUS = {
  proposed: { label: "Proposé à l'équipe", style: { background: "#FEF3C7", color: "#92400E" } },
  approved: { label: "Ajouté à l'équipe", style: { background: "#D1FAE5", color: "#065F46" } },
  rejected: { label: "Non retenu", style: { background: "#E5E7EB", color: "#374151" } },
};
const short = (s, n = 60) => (s.length > n ? `${s.slice(0, n)}…` : s);
const err = (e) => toast.error(e?.friendlyMessage || e?.response?.data?.detail || "Erreur");

export const useQuickReplies = () => {
  const [data, setData] = useState({ team: [], mine: [], proposals: [], is_super: false, personal_on: true });
  const reload = useCallback(() => api.get("/admin/tickets/quick-replies").then(({ data: d }) => setData({ proposals: [], ...d })).catch(() => {}), []);
  useEffect(() => { reload(); }, [reload]);
  const call = async (fn, ok) => { try { await fn(); if (ok) toast.success(ok); await reload(); } catch (e) { err(e); } };
  return {
    data, reload,
    saveTeam: (list) => call(() => api.patch("/admin/settings", { ticket_templates: list }), "Modèles d'équipe enregistrés"),
    addMine: (text) => call(() => api.post("/admin/tickets/quick-replies", { text }), "Modèle enregistré"),
    removeMine: (id) => call(() => api.delete(`/admin/tickets/quick-replies/${id}`)),
    propose: (id) => call(() => api.post(`/admin/tickets/quick-replies/${id}/propose`), "Proposé à l'équipe — le super admin décide"),
    decide: (id, approve) => call(() => api.post(`/admin/tickets/quick-replies/${id}/decide`, { approve }), approve ? "Ajouté aux modèles d'équipe" : "Proposition refusée"),
  };
};

const AddRow = ({ placeholder, onAdd, testId }) => {
  const [v, setV] = useState("");
  return (
    <div className="flex gap-2">
      <Input value={v} onChange={(e) => setV(e.target.value)} placeholder={placeholder} maxLength={600} data-testid={`${testId}-input`} />
      <Button size="sm" className="pill-primary rounded-full shrink-0" disabled={!v.trim()} onClick={() => { onAdd(v.trim()); setV(""); }} data-testid={`${testId}-add`}><Plus size={14} /></Button>
    </div>
  );
};

// Liste des propositions en attente (super admin) : Approuver → modèle d'équipe ; Refuser.
export const ProposalsList = ({ proposals, onDecide, testId = "quick-reply-proposals" }) => (
  <div className="space-y-1.5" data-testid={testId}>
    {proposals.map((p) => (
      <div key={p.id} className="flex items-center gap-2 text-sm rounded-lg px-2 py-1.5" style={{ background: "#FFFBEB" }} data-testid={`quick-reply-proposal-${p.id}`}>
        <Lightbulb size={13} className="shrink-0" style={{ color: "#B45309" }} />
        <span className="flex-1 min-w-0"><b className="text-xs">{p.user_name || "Un agent"}</b> · <span title={p.text}>{short(p.text, 110)}</span></span>
        <Button size="sm" className="pill-primary rounded-full h-7 px-2.5 text-xs" onClick={() => onDecide(p.id, true)} data-testid={`quick-reply-approve-${p.id}`}><Check size={12} className="mr-1" />Approuver</Button>
        <Button size="sm" variant="outline" className="rounded-full h-7 px-2.5 text-xs" onClick={() => onDecide(p.id, false)} data-testid={`quick-reply-reject-${p.id}`}>Refuser</Button>
      </div>
    ))}
  </div>
);

// Carte de la LISTE des tickets (super admin) : propositions à traiter, visibles sans ouvrir un ticket.
export const QuickReplyProposalsCard = ({ isSuper }) => {
  const { data, decide } = useQuickReplies();
  if (!isSuper || !(data.proposals || []).length) return null;
  return (
    <div className="yalola-card p-4 space-y-2" data-testid="quick-reply-proposals-card">
      <div className="text-xs font-semibold uppercase tracking-wide flex items-center gap-1.5" style={{ color: "var(--yalola-text-muted)" }}><Lightbulb size={13} style={{ color: "#B45309" }} /> {data.proposals.length} modèle{data.proposals.length > 1 ? "s" : ""} de réponse proposé{data.proposals.length > 1 ? "s" : ""} par l'équipe</div>
      <ProposalsList proposals={data.proposals} onDecide={decide} />
    </div>
  );
};

// Barre sous le fil d'un ticket : chips ⚡ équipe + 👤 perso, « Gérer » ouvre l'éditeur (équipe / mes modèles / propositions).
export const QuickRepliesBar = ({ onPick, canManage, isSuper }) => {
  const personalGuard = useGuard("support_personal_replies");
  const { data, saveTeam, addMine, removeMine, propose, decide } = useQuickReplies();
  const [manage, setManage] = useState(false);
  const personalOn = personalGuard && data.personal_on !== false;
  const team = data.team || [], mine = personalOn ? data.mine || [] : [];
  const empty = team.length === 0 && mine.length === 0;
  return (
    <div className="space-y-2" data-testid="quick-replies">
      <div className="flex flex-wrap gap-1.5" data-testid="ticket-templates-row">
        {team.map((tpl, i) => (
          <button key={`t${i}`} type="button" onClick={() => onPick(tpl)} className="chip text-xs hover:opacity-80 max-w-[280px] truncate" title={tpl} data-testid={`ticket-template-${i}`}>
            <Zap size={10} className="inline mr-1" />{short(tpl)}
          </button>
        ))}
        {mine.map((m) => (
          <button key={m.id} type="button" onClick={() => onPick(m.text)} className="chip text-xs hover:opacity-80 max-w-[280px] truncate" style={{ background: "#F5F3FF", color: "#5B21B6", borderColor: "#DDD6FE" }} title={`Mon modèle : ${m.text}`} data-testid={`ticket-my-template-${m.id}`}>
            <UserRound size={10} className="inline mr-1" />{short(m.text)}
          </button>
        ))}
        {canManage && (
          <button type="button" onClick={() => setManage((v) => !v)} className="chip text-xs hover:opacity-80" data-testid="ticket-templates-manage">
            {empty ? <><Plus size={11} className="inline mr-1" />Ajouter des réponses rapides</> : <><Settings2 size={11} className="inline mr-1" />Gérer</>}
          </button>
        )}
      </div>
      {manage && canManage && (
        <div className="rounded-xl p-3 space-y-3" style={{ background: "var(--yalola-surface-alt)" }} data-testid="ticket-templates-editor">
          <div className="space-y-1.5" data-testid="quick-replies-team">
            <div className="text-[11px] font-semibold uppercase tracking-wide flex items-center gap-1" style={{ color: "var(--yalola-text-muted)" }}><Zap size={11} /> Modèles d'équipe{!isSuper && <span className="font-normal normal-case"> · gérés par le super admin</span>}</div>
            {team.length === 0 && <div className="text-xs" style={{ color: "var(--yalola-text-muted)" }}>Aucun modèle d'équipe.</div>}
            {team.map((tpl, i) => (
              <div key={i} className="flex items-center gap-2 text-sm">
                <span className="flex-1 truncate" title={tpl}>{tpl}</span>
                {isSuper && <button type="button" onClick={() => saveTeam(team.filter((_, j) => j !== i))} style={{ color: "#B91C1C" }} aria-label="Supprimer ce modèle d'équipe" data-testid={`ticket-template-del-${i}`}><X size={14} /></button>}
              </div>
            ))}
            {isSuper && <AddRow placeholder="Nouveau modèle d'équipe…" onAdd={(t) => saveTeam([...team, t])} testId="ticket-template-new" />}
          </div>
          {personalOn && (
            <div className="space-y-1.5" data-testid="quick-replies-mine">
              <div className="text-[11px] font-semibold uppercase tracking-wide flex items-center gap-1" style={{ color: "#5B21B6" }}><UserRound size={11} /> Mes modèles <span className="font-normal normal-case">· visibles par moi seulement ({mine.length}/{data.max || 30})</span></div>
              {mine.length === 0 && <div className="text-xs" style={{ color: "var(--yalola-text-muted)" }}>Enregistre tes propres réponses types : elles n'apparaissent que pour toi.</div>}
              {mine.map((m) => {
                const st = STATUS[m.status];
                return (
                  <div key={m.id} className="flex items-center gap-2 text-sm" data-testid={`quick-reply-mine-${m.id}`}>
                    <span className="flex-1 truncate" title={m.text}>{m.text}</span>
                    {st && <span className="chip text-[10px] shrink-0" style={st.style} data-testid={`quick-reply-status-${m.id}`}>{st.label}</span>}
                    {!m.status || m.status === "rejected" ? (
                      <button type="button" onClick={() => propose(m.id)} className="chip text-[10px] shrink-0 hover:opacity-80" title="Proposer ce modèle à toute l'équipe (le super admin valide)" data-testid={`quick-reply-propose-${m.id}`}><Send size={10} className="inline mr-1" />Partager à l'équipe</button>
                    ) : null}
                    <button type="button" onClick={() => removeMine(m.id)} style={{ color: "#B91C1C" }} aria-label="Supprimer mon modèle" data-testid={`quick-reply-del-${m.id}`}><X size={14} /></button>
                  </div>
                );
              })}
              <AddRow placeholder="Nouveau modèle personnel…" onAdd={addMine} testId="quick-reply-new" />
            </div>
          )}
          {isSuper && (data.proposals || []).length > 0 && (
            <div className="space-y-1.5">
              <div className="text-[11px] font-semibold uppercase tracking-wide flex items-center gap-1" style={{ color: "#B45309" }}><Lightbulb size={11} /> Propositions de l'équipe ({data.proposals.length})</div>
              <ProposalsList proposals={data.proposals} onDecide={decide} testId="quick-replies-proposals" />
            </div>
          )}
        </div>
      )}
    </div>
  );
};
